import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  Button,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  NewspaperOutlined,
  People as PeopleIcon,
} from "@mui/icons-material";
// import Sidebar from '../components/Sidebar';
import moneyTooLogo from "../assets/money-too-logo.svg";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
// import FtpIcon from "../components/FtpIcon";
// import UploadSchedulerIcon from "../components/UploadSchedulerIcon";
import ContentHubIcon from "../components/ContentHubIcon";
import VotrAccessIcon from "../components/VotrAccessIcon";

const Home: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Define role-based menu items with icons
  const allMenuItems = [
    {
      text: "CUSIP Management",
      icon: <NewspaperOutlined />,
      path: "/cusips",
      roles: ["Admin"],
    },
    {
      text: "Shareholders",
      icon: <PeopleIcon />,
      path: "/shareholders",
      roles: ["Admin"],
    },
    // { text: "FTPs", icon: <FtpIcon />, path: "/ftps", roles: ["Admin"] },
    // {
    //   text: "Upload Scheduler",
    //   icon: <UploadSchedulerIcon />,
    //   path: "/upload-scheduler",
    //   roles: ["Admin"],
    // },
    {
      text: "Content Hub",
      icon: <ContentHubIcon />,
      path: "/content-hub",
      roles: ["Admin"],
    },
    {
      text: "Dashboard",
      icon: <ContentHubIcon />,
      path: "/dashboard",
      roles: [],
    },
    {
      text: "Portfolio",
      icon: <PeopleIcon />,
      path: "/portfolio",
      roles: ["User"],
    },
    {
      text: "VOTR Access",
      icon: <VotrAccessIcon />,
      path: "/votr-access",
      roles: ["Admin", "User"],
    },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role.name))
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
      }}
    >
      {/* Responsive Navbar */}
      <AppBar
        position="fixed"
        sx={{
          width: "100%",
          ml: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          bgcolor: "#18010a",
          color: "white",
          margin: "0 auto",
          zIndex: theme.zIndex.drawer,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "70px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 2,
            maxWidth: "1340px",
            width: "100%",
            px: 3,
          }}
        >
          {/* Left Side - Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            <img
              src={moneyTooLogo}
              alt="MoneyToo! Logo"
              style={{
                width: "46px",
                height: "25px",
                marginRight: "12px",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "white",
                display: { xs: "none", sm: "block" },
                fontSize: "1rem",
                letterSpacing: "0.5px",
              }}
            >
              Money Too!
            </Typography>
          </Box>

          {/* Center - Navigation Items (Desktop Only) */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              display: { xs: "none", md: "flex" },
              flex: 1,
              justifyContent: "center",
            }}
          >
            {menuItems.map((item) => (
              <Button
                key={item.text}
                onClick={() => navigate(item.path)}
                sx={{
                  color: location.pathname === item.path ? "#C10B50" : "white",
                  bgcolor:
                    location.pathname === item.path
                      ? "rgba(193, 11, 80, 0.1)"
                      : "transparent",
                  "&:hover": {
                    bgcolor:
                      location.pathname === item.path
                        ? "rgba(193, 11, 80, 0.2)"
                        : "rgba(255, 255, 255, 0.1)",
                  },
                  textTransform: "none",
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  px: { xs: 0.6, lg: 1.5 },
                  py: { xs: 0.5, lg: 0.75 },
                  borderRadius: 2,
                  fontSize: {
                    xs: "0.625rem",
                    sm: "0.65rem",
                    md: "0.65rem",
                    lg: "0.8rem",
                  },
                }}
              >
                {item.text}
              </Button>
            ))}
          </Stack>

          {/* Right Side - Desktop Logout & Mobile Hamburger */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Desktop Logout */}
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                color: "white",
                textTransform: "none",
                display: { xs: "none", md: "flex" },
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                },
                borderRadius: 2,
                fontSize: {
                  xs: "0.625rem",
                  sm: "0.65rem",
                  md: "0.7rem",
                  lg: "0.8rem",
                },
              }}
            >
              Logout
            </Button>

            {/* Mobile Hamburger Menu */}
            <IconButton
              color="inherit"
              aria-label="open menu"
              onClick={handleMobileMenuToggle}
              sx={{
                display: { xs: "block", md: "none" },
                ml: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            bgcolor: "#18010a",
            color: "white",
            boxShadow: "2px 0 8px rgba(0,0,0,0.3)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Mobile Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              pb: 2,
            }}
          >
            <img
              src={moneyTooLogo}
              alt="MoneyToo! Logo"
              style={{
                width: "46px",
                height: "25px",
                marginRight: "16px",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "white",
                fontSize: { xs: "1.125rem", sm: "1.35rem" },
              }}
            >
              Money Too!
            </Typography>
          </Box>

          {/* User Info */}
          {user && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "rgba(255,255,255,0.05)",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#8C8E9C",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Welcome back,
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {user.profile?.fullName || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#8C8E9C",
                  fontSize: { xs: "0.625rem", sm: "0.75rem" },
                }}
              >
                {user.role?.name || "User"}
              </Typography>
            </Box>
          )}

          {/* Navigation Items */}
          <List sx={{ flexGrow: 1 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    color: "#8C8E9C",
                    "&.Mui-selected": {
                      bgcolor: "#2e0c19",
                      color: "#C10B50",
                    },
                    "&:hover": {
                      bgcolor: "#2e0c19",
                    },
                    minHeight: 48,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 30,
                      mr: 2,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Mobile Logout */}
          <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 2 }} />
          <Button
            fullWidth
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              color: "#8C8E9C",
              justifyContent: "flex-start",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#2e0c19",
              },
              minHeight: 48,
              borderRadius: 2,
              fontSize: { xs: "0.6rem", sm: "0.8rem" },
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>

      {/* Commented out Sidebar */}
      {/* <Sidebar
                collapsed={collapsed}
                onCollapseToggle={handleCollapseToggle}
                mobileOpen={mobileOpen}
                onDrawerToggle={handleDrawerToggle}
            /> */}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: "100%",
          mt: "64px",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Home;
